import StreamIcon from '@mui/icons-material/Stream';
import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import ContextTag from '../../../../Common/ContextTag';
import { CommandContextFieldsFragment, useGetLiveProjectCommandContextsQuery } from '../../../../generated/core-api/graphql-react-query';
import DurationFormat from '../../../../utils/formatDuration';
import useGlobalStoreContext from '../../../Contexts/GlobalContext/Contexts/GlobalStoreContext/useGlobalStoreContext';
import useHistory from '../../../Hooks/useHistory/useHistory';
import useIsSessionLivePage from '../../../Hooks/useIsSessionLivePage';
import useSessionContext from '../../../LiveContext/SessionContextProvider/useSessionContext';
import UpdateBadge from '../../SessionPanel/Components/UpdateBadge';
import useDetectionGroups from '../../SessionPanel/Contexts/DetectionGroupContext/useDetectionGroups';
import OverflowItem from '../Overflow/OverflowItem';
import StyledBox from './styles';

interface SessionInformationProps {
  tab: { index: number, updatedAt: number };
  setTab: (value: { index: number, updatedAt: number }) => void;
}

function toEpochMs(value: unknown): number | null {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return value > 1e12 ? value : value * 1000;
  }
  if (typeof value === 'string') {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue > 1e12 ? numericValue : numericValue * 1000;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

export default function SessionInformation(props: SessionInformationProps) {
  const { tab, setTab } = props;
  const { t, i18n } = useTranslation();
  const durationFormat = new DurationFormat({ style: 'narrow', timestampFormat: 'millisecond' });
  const {
    sessionId, session, latestDetection,
  } = useSessionContext();
  const { projectId: [projectId] } = useGlobalStoreContext();
  const allDetections = useDetectionGroups();
  const unseenDetections = React.useMemo(() => allDetections.filter((detectionGroup) => !!detectionGroup.notificationBadge && !detectionGroup.notificationBadge.seen), [allDetections]);
  const { context } = useSessionContext();
  const contexts = useGetLiveProjectCommandContextsQuery({ input: { id: projectId ?? '' } }, { enabled: !!projectId });
  const isLiveSessionPage = useIsSessionLivePage();

  const contextMap = React.useMemo(() => {
    const map = new Map<string, CommandContextFieldsFragment>();
    contexts.data?.getProject.commandContexts.forEach((value) => {
      map.set(value.textLower, value);
    });
    return map;
  }, [contexts.data]);

  const chosenContext = React.useMemo(() => {
    if (!context) return null;
    return contextMap.get(context ?? '');
  }, [context, contextMap]);

  const { newHistoryState } = useHistory();
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { timeStyle: 'short' });
  const sessionStartedAtMs = React.useMemo(() => toEpochMs(session?.date), [session?.date]);
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!sessionId || sessionStartedAtMs === null) return undefined;

    setNow(Date.now());
    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [sessionId, sessionStartedAtMs]);

  const sessionDuration = React.useMemo(() => {
    if (sessionStartedAtMs === null) return 0;
    return Math.max(0, now - sessionStartedAtMs);
  }, [now, sessionStartedAtMs]);

  if (!sessionId) return null;

  return (
    <StyledBox>
      <OverflowItem position="left" id="session-info" priority={11}>
        <Box className="informations">
          <Box className="info">
            <Typography variant="subtitle1">
              {`${dateFormat.format(Date.now())} | ${durationFormat.format(sessionDuration)}`}
            </Typography>
          </Box>
          {chosenContext && (
            <Box className="info">
              <ContextTag context={chosenContext} />
            </Box>
          )}
        </Box>
      </OverflowItem>
      {!isLiveSessionPage && (
        <OverflowItem position="left" id="live-session-page" priority={2}>
          <UpdateBadge seenAt={tab.updatedAt} updatedAt={latestDetection?.cardId && latestDetection?.date} disabled={false} badgeContent={unseenDetections.length}>
            <Button
              component={NavLink}
              variant="text"
              title={t('Live session page')}
              disabled={isLiveSessionPage}
              onClick={() => { setTab({ index: 0, updatedAt: Date.now() }); }}
              to="/live-session"
              state={newHistoryState}
            >
              <StreamIcon />
              {t('Live session page')}
            </Button>
          </UpdateBadge>
        </OverflowItem>
      )}
    </StyledBox>
  );
}
